import { router } from '@inertiajs/react'
import { declineRateLimitDialog } from './rate_limit'

/**
 * After a history restore of a map page, asks for the reader's own marks
 * again, once.
 *
 * `my_tastings` is kept in browser history with the page. He opens the map,
 * goes to his journal, adds a wine or pins a producer there, and comes Back:
 * the entry he lands on holds his marks as they were when he left. The
 * journal's list and the winery's panel ask again for the same reason
 * (lib/use_entry_memory.ts). The bare map and a place's page had nothing that
 * did, so their marks stayed as they were until the next page the server
 * answered.
 *
 * HERE, FOR THE WHOLE DOCUMENT, AND NOT IN THE MAP'S SHELL, for three reasons:
 *
 *   - The shell is one instance across every map page, so "was this page
 *     restored" cannot be asked once at mount, which is how `useEntryMemory`
 *     asks it. Back from a winery to a place mounts nothing.
 *   - `useEntryMemory` is a chunk of its own, and the shell's chunk and the
 *     home page's preloads are held to the files they have
 *     (test/controllers/boot_path_test.rb). This module is imported by the
 *     entry alone, so it is part of the entry and adds no file to any page.
 *   - A restore is something the browser announces: `popstate`, and then
 *     Inertia's `navigate` once it has put the entry's page back. A visit
 *     fires `navigate` with no `popstate` before it.
 *
 * A SIGNED-IN READER ONLY. The atlas is public and rate-limited, a visitor
 * has no marks, and Back must cost the server nothing for them.
 *
 * ON A PLACE'S PAGE, THE PLACE WITH THEM, IN THE SAME REQUEST. Its panel lists
 * his tastings there and what is on his wishlist there, and both lists are
 * inside the `place` prop (PlacesController#place_prop), kept in the same
 * history entry. With the tasting marks asked for alone, the map counted the
 * wine he had just added and "My tastings here" beside it did not. `place` is
 * one lambda, so its rows come only with the whole panel. That is the cost of
 * one visit to the place, paid once for a restore, by a signed-in reader. The
 * camera does not move for it: the map moves when the place's slug changes,
 * not when its object does (use_maplibre.ts).
 *
 * And the wishlist's marks there, `my_wishlist`, because the place brings its
 * wishlist rows and the same disagreement would open between those two. On
 * the bare map and a grape's page nothing lists the wishlist, and its marks
 * are left as the entry had them.
 *
 * NOT ON A WINERY'S PAGE. Its panel asks for its rows and the marks in one
 * request, so that the rows and the mark beside them agree
 * (components/map/winery_my_wines.tsx). A second request from here would be
 * the same marks twice.
 *
 * QUIET WHEN IT FAILS, as that panel's is: he did not ask for it. A refusal
 * must not open Inertia's dialog over the map, and a dropped connection must
 * not be a rejection nobody catches. The marks, and a place's rows, then stay
 * as the entry had them.
 *
 * Where Inertia cannot put the entry back (its history key is gone) it visits
 * the address afresh, and this then asks for what that visit has just
 * brought. One request too many, and only then.
 */
export function refreshMarksAfterRestore() {
  let restoring = false

  // A change of `#fragment` fires this too, with no state, and Inertia lands
  // no page for it.
  window.addEventListener('popstate', (event) => {
    restoring = event.state !== null
  })

  router.on('navigate', (event) => {
    if (!restoring) return
    restoring = false

    const { component, props } = event.detail.page
    const signedIn = (props.auth as { signed_in?: boolean } | undefined)?.signed_in === true
    if (!signedIn || !('my_tastings' in props) || component === WINERY_PAGE) return

    router.reload({
      only: component === PLACE_PAGE ? PLACE_AND_MARKS : MARKS,
      onHttpException: declineRateLimitDialog,
      onNetworkError: () => false,
    })
  })
}

// As `render inertia:` names them (WineriesController#show, PlacesController#show).
const WINERY_PAGE = 'wineries/show'
const PLACE_PAGE = 'places/show'

const MARKS = ['my_tastings']
const PLACE_AND_MARKS = ['my_tastings', 'my_wishlist', 'place']
